import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { MessageSquare, Plus, Loader2, Megaphone, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TOPICS = ["All", "Families Support", "Legal Guidance", "General Questions"];
const POST_TOPICS = TOPICS.filter((t) => t !== "All");

const PETITION_CATEGORIES = [
  "Criminal Justice Reform",
  "Parole Reform",
  "Conditions of Confinement",
  "Family Rights",
  "Reentry Support",
  "Other",
];

const EMPTY_PETITION = {
  title: "", category: "", cause: "", impact: "", proposed_action: "",
  target_audience: "", requester_name: "", requester_email: "", requester_phone: "",
  affiliation: "", consent_share: false,
};

function LivePetitions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/petitions");
        setItems(data);
      } catch (err) {
        console.warn("Failed to load live petitions:", err);
      }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <div className="mb-10" data-testid="live-petitions">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="display-serif text-2xl text-zinc-50 accent-rule">Live petitions</h2>
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Sign · share · amplify</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((p) => (
          <Link key={p.slug} to={`/petitions/${p.slug}`} className="group border border-zinc-800 hover:border-amber-400/60 bg-zinc-950/60 rounded-sm p-5 transition-colors" data-testid={`live-petition-${p.slug}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] font-mono tracking-[0.18em] uppercase text-amber-400 border border-amber-400/30 rounded-sm px-2 py-0.5">{p.category}</span>
              <span className="text-xs text-amber-400">{p.signature_count.toLocaleString()} signatures</span>
            </div>
            <h3 className="display-serif text-lg text-zinc-50 group-hover:text-amber-300 transition-colors mb-1.5">{p.title}</h3>
            <p className="text-sm text-zinc-400 line-clamp-2 leading-relaxed">{p.cause}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

function PetitionBanner() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(EMPTY_PETITION);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/petitions/request", form);
      setSubmitted(true);
      toast.success("Petition submitted for review. You'll hear from us soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_PETITION);
    setSubmitted(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 200); }}>
      <DialogTrigger asChild>
        <button
          className="group w-full text-left border border-amber-400/40 bg-gradient-to-br from-blue-950/40 to-black/40 hover:from-blue-950/60 hover:to-black/60 rounded-sm p-6 lg:p-7 transition-colors flex flex-col sm:flex-row items-start sm:items-center gap-5"
          data-testid="petition-banner"
        >
          <div className="flex-shrink-0 h-12 w-12 rounded-sm border border-amber-400/60 bg-blue-950/40 flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-400">Take action</p>
              <span className="h-1 w-1 rounded-full bg-zinc-700" />
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">Community submission</p>
            </div>
            <h3 className="display-serif text-2xl text-zinc-50 leading-tight mb-1.5 group-hover:text-amber-300 transition-colors">
              Start a petition.
            </h3>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-xl">
              Have an issue worth fighting for? Tell us your cause and we'll review it. Approved petitions get organized, formatted, and amplified through our network.
            </p>
          </div>
          <span className="text-amber-400 text-sm hidden sm:inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
            Submit petition <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </span>
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl bg-zinc-950 border border-amber-400/30 text-zinc-100 p-0 max-h-[90vh] flex flex-col rounded-sm">
        {submitted ? (
          <div className="px-6 sm:px-8 py-12 text-center" data-testid="petition-success">
            <CheckCircle2 className="h-12 w-12 text-amber-400 mx-auto mb-5" strokeWidth={1.5} />
            <p className="overline mb-3 justify-center">Submission received</p>
            <h3 className="display-serif text-3xl text-zinc-50 mb-3">Thank you.</h3>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-md mx-auto mb-7">
              Your petition idea is now in our review queue. Our team reviews submissions on a rolling basis and will reach out by email if we move forward together.
            </p>
            <Button onClick={reset} variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30" data-testid="petition-close-btn">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 sm:px-8 pt-7 pb-4 border-b border-zinc-800">
              <p className="overline mb-3">Petition request</p>
              <DialogTitle className="display-serif text-2xl sm:text-3xl text-zinc-50 leading-tight">
                Start a petition.
              </DialogTitle>
              <p className="text-sm text-zinc-400 leading-relaxed mt-2">
                Tell us your cause. We'll review and follow up with next steps.
              </p>
            </DialogHeader>

            <form onSubmit={submit} className="overflow-y-auto flex-1 px-6 sm:px-8 py-6 space-y-4" data-testid="petition-form">
              <div>
                <label className="text-zinc-300 text-sm">Petition Title <span className="text-amber-400">*</span></label>
                <Input required value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g., Reform Parole Decision Standards in New York" className="bg-black border-zinc-800 mt-1.5" data-testid="petition-title"/>
              </div>

              <div>
                <label className="text-zinc-300 text-sm">Category <span className="text-amber-400">*</span></label>
                <Select value={form.category} onValueChange={(v) => update("category", v)} required>
                  <SelectTrigger className="bg-black border-zinc-800 mt-1.5 text-zinc-100" data-testid="petition-category"><SelectValue placeholder="Choose a category"/></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {PETITION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-zinc-300 text-sm">The Cause <span className="text-amber-400">*</span></label>
                <Textarea required rows={4} value={form.cause} onChange={(e) => update("cause", e.target.value)} placeholder="What change are you fighting for? Be specific." className="bg-black border-zinc-800 mt-1.5" data-testid="petition-cause"/>
              </div>

              <div>
                <label className="text-zinc-300 text-sm">Why this matters</label>
                <Textarea rows={3} value={form.impact} onChange={(e) => update("impact", e.target.value)} placeholder="Who is affected? What's the human cost of the status quo?" className="bg-black border-zinc-800 mt-1.5" data-testid="petition-impact"/>
              </div>

              <div>
                <label className="text-zinc-300 text-sm">Proposed Action</label>
                <Textarea rows={2} value={form.proposed_action} onChange={(e) => update("proposed_action", e.target.value)} placeholder="What specific outcome are you asking for?" className="bg-black border-zinc-800 mt-1.5" data-testid="petition-action"/>
              </div>

              <div>
                <label className="text-zinc-300 text-sm">Target Audience</label>
                <Input value={form.target_audience} onChange={(e) => update("target_audience", e.target.value)} placeholder="e.g., NYS Legislature, DOCCS leadership, Parole Board" className="bg-black border-zinc-800 mt-1.5" data-testid="petition-target"/>
              </div>

              <div className="border-t border-zinc-800 pt-5">
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-3">Your contact info</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-300 text-sm">Your Name <span className="text-amber-400">*</span></label>
                    <Input required value={form.requester_name} onChange={(e) => update("requester_name", e.target.value)} className="bg-black border-zinc-800 mt-1.5" data-testid="petition-name"/>
                  </div>
                  <div>
                    <label className="text-zinc-300 text-sm">Email <span className="text-amber-400">*</span></label>
                    <Input type="email" required value={form.requester_email} onChange={(e) => update("requester_email", e.target.value)} className="bg-black border-zinc-800 mt-1.5" data-testid="petition-email"/>
                  </div>
                  <div>
                    <label className="text-zinc-300 text-sm">Phone</label>
                    <Input value={form.requester_phone} onChange={(e) => update("requester_phone", e.target.value)} className="bg-black border-zinc-800 mt-1.5" data-testid="petition-phone"/>
                  </div>
                  <div>
                    <label className="text-zinc-300 text-sm">Affiliation (optional)</label>
                    <Input value={form.affiliation} onChange={(e) => update("affiliation", e.target.value)} placeholder="Organization, group" className="bg-black border-zinc-800 mt-1.5" data-testid="petition-affiliation"/>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 border border-zinc-800 rounded-sm px-4 py-3 bg-zinc-950/60 cursor-pointer mt-3">
                <input type="checkbox" checked={form.consent_share} onChange={(e) => update("consent_share", e.target.checked)} className="mt-1 accent-amber-400" data-testid="petition-consent"/>
                <span className="text-xs text-zinc-300 leading-relaxed">
                  I consent to Paralegal Voices of New York reviewing this submission and contacting me about next steps. I understand this is a review request — not a guarantee of action.
                </span>
              </label>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-zinc-800">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm text-zinc-300 hover:text-zinc-100">Cancel</Button>
                <Button type="submit" disabled={submitting || !form.consent_share} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="petition-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : <>Submit for review <ArrowRight className="ml-2 h-4 w-4"/></>}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PostsListSection({ loading, posts }) {
  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400 mx-auto" />
      </div>
    );
  }
  if (posts.length === 0) {
    return (
      <div className="text-center py-16 border border-zinc-800 rounded-sm" data-testid="no-posts">
        <MessageSquare className="h-8 w-8 text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
        <p className="text-zinc-400">No posts yet. Be the first to start a conversation.</p>
      </div>
    );
  }
  return (
    <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800" data-testid="posts-list">
      {posts.map((p) => (
        <Link
          key={p.id}
          to={`/community/${p.id}`}
          className="block p-6 hover:bg-black transition-colors group"
          data-testid={`post-${p.id}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="rounded-sm text-xs border-amber-400/30 text-amber-400">
                  {p.topic}
                </Badge>
                <span className="text-xs text-zinc-400">
                  {new Date(p.created_at).toLocaleDateString()}
                </span>
              </div>
              <h3 className="text-lg font-medium text-zinc-50 group-hover:text-amber-400 transition-colors mb-1">
                {p.title}
              </h3>
              <p className="text-sm text-zinc-400 line-clamp-2">{p.content}</p>
              <p className="text-xs text-zinc-500 mt-2">by {p.author_name}</p>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-zinc-400 whitespace-nowrap">
              <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
              {p.comment_count}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTopic, setActiveTopic] = useState("All");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", topic: "General Questions" });

  const fetchPosts = useCallback(async (topic) => {
    setLoading(true);
    try {
      const params = topic && topic !== "All" ? { topic } : {};
      const { data } = await api.get("/forum/posts", { params });
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(activeTopic); }, [activeTopic, fetchPosts]);

  const submit = async () => {
    if (!user) {
      toast.error("Please sign in to create a post");
      navigate("/login");
      return;
    }
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/forum/posts", form);
      toast.success("Post created");
      setForm({ title: "", content: "", topic: "General Questions" });
      setOpen(false);
      fetchPosts(activeTopic);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="community-page">
      <section className="border-b border-zinc-800">
        <div className="container-pl py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div>
              <p className="overline mb-4">Community</p>
              <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-zinc-50 mb-3">
                Forum & Discussions
              </h1>
              <p className="text-zinc-300 max-w-xl leading-relaxed">
                A safe space to ask questions, share experiences, and connect with others navigating similar situations.
              </p>
            </div>
            <div>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white h-11"
                    onClick={(e) => {
                      if (!user) {
                        e.preventDefault();
                        toast.error("Please sign in to create a post");
                        navigate("/login");
                      }
                    }}
                    data-testid="new-post-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm">
                  <DialogHeader>
                    <DialogTitle>Create a new post</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-zinc-200 mb-1.5 block">Topic</label>
                      <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="post-topic">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {POST_TOPICS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-200 mb-1.5 block">Title</label>
                      <Input
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="rounded-sm"
                        placeholder="What's your question?"
                        data-testid="post-title"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-200 mb-1.5 block">Content</label>
                      <Textarea
                        value={form.content}
                        onChange={(e) => setForm({ ...form, content: e.target.value })}
                        className="rounded-sm min-h-[140px]"
                        placeholder="Share the details..."
                        data-testid="post-content"
                      />
                    </div>
                    <Button
                      onClick={submit}
                      disabled={submitting}
                      className="w-full rounded-sm bg-blue-700 hover:bg-blue-600"
                      data-testid="submit-post-btn"
                    >
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Publish post
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      <section className="container-pl py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="topic-tabs">
          {TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTopic(t)}
              className={`px-4 py-1.5 text-sm border rounded-sm transition-colors ${
                activeTopic === t
                  ? "border-amber-400 bg-blue-700 text-white"
                  : "border-zinc-800 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
              }`}
              data-testid={`topic-${t.toLowerCase().replace(" ", "-")}`}
            >
              {t}
            </button>
          ))}
        </div>

        <PostsListSection loading={loading} posts={posts} />

        <div className="mt-14 pt-10 border-t border-zinc-900">
          <PetitionBanner />
        </div>

        <div className="mt-10">
          <LivePetitions />
        </div>
      </section>
    </div>
  );
}
